import React from 'react';
import {Text, TextInput, TouchableOpacity, View} from 'react-native';
import I18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  groupBg,
  groupBorderColor,
  groupRadius,
  textInputBg,
  textInputRadius,
  textInputText,
  textMutedColor,
  touchTargetMin
} from '../../../style/adminTools';
import {
  Body,
  PanelButton,
  PanelFrame,
  Title
} from '../tenantGrid/GridStateBanner';

const t = (key, defaultValue, options) =>
  I18n.t(`adminTools.${key}`, {defaultValue, ...options});

const SCOPE_TEXT = {
  allTeam: tenant => I18n.t('adminTools.searchScopeAllTeam', {tenant}),
  allCustomer: tenant =>
    t(
      'searchScopeAllCustomer',
      'Searched the GroovePacker team band and the Customer band. All matches are in the customer band, so {{tenant}} can see them.',
      {tenant}
    ),
  both: tenant =>
    t(
      'searchScopeSomeCustomer',
      'Searched the GroovePacker team band and the Customer band. Some matches are in the customer band, so {{tenant}} can see those.',
      {tenant}
    )
};

const searchCountText = ({totalMatches, groupsHit}) =>
  I18n.t('adminTools.searchMatchCount', {
    count: totalMatches,
    groups: groupsHit,
    groupsWord: I18n.t('adminTools.searchGroupsWord', {count: groupsHit})
  });

export const groupCountLabelFor = count =>
  count > 0 ? I18n.t('adminTools.groupMatchCount', {count}) : '0';

export const SearchNoMatchesPanel = ({query, onClear, phone}) => (
  <PanelFrame testID="settingsSearchNoMatches" phone={phone}>
    <Title>
      {t('searchNoMatchesTitle', 'No settings match "{{query}}"', {query})}
    </Title>
    <Body>
      {t(
        'searchNoMatchesBody',
        'Searched names, tooltips and group names in both bands.'
      )}
    </Body>
    <PanelButton
      primary
      block={phone}
      testID="settingsSearchNoMatchesClear"
      label={t('searchClear', 'Clear search')}
      onPress={onClear}
    />
  </PanelFrame>
);

const SettingsSearchBar = ({
  value,
  onChange,
  onClear,
  search,
  tenantName,
  phone
}) => {
  const searching = Boolean(search && search.active);
  const clearLabel = t('searchClear', 'Clear search');
  const placeholder = I18n.t(
    phone ? 'adminTools.searchPlaceholderShort' : 'adminTools.searchPlaceholder'
  );
  const scopeText =
    searching && SCOPE_TEXT[search.scope]
      ? SCOPE_TEXT[search.scope](tenantName)
      : null;

  return (
    <View
      testID="settingsSearchBar"
      style={[
        {marginBottom: 12},
        phone
          ? {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: groupBg,
              borderWidth: 1,
              borderColor: groupBorderColor,
              borderRadius: groupRadius,
              padding: 8
            }
          : null
      ]}
    >
      <View
        testID="settingsSearchField"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          height: touchTargetMin,
          paddingLeft: 12,
          backgroundColor: textInputBg,
          borderRadius: textInputRadius,
          borderWidth: 1,
          borderColor: searching ? accentColor : 'transparent'
        }}
      >
        <TextInput
          testID="settingsSearchInput"
          value={value}
          onChangeText={onChange}
          onKeyPress={event => {
            if (event.nativeEvent && event.nativeEvent.key === 'Escape')
              onClear();
          }}
          placeholder={placeholder}
          placeholderTextColor={textMutedColor}
          accessibilityLabel={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            height: touchTargetMin,
            outlineStyle: 'none',
            ...textInputText,
            fontSize: phone ? 16 : textInputText.fontSize
          }}
        />
        {value ? (
          <TouchableOpacity
            testID="settingsSearchClear"
            accessibilityRole="button"
            accessibilityLabel={clearLabel}
            onPress={onClear}
            style={{
              width: touchTargetMin,
              height: touchTargetMin,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text style={{color: textMutedColor, fontSize: 20}}>{'×'}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {searching && search.totalMatches > 0 ? (
        <View
          testID="settingsSearchCount"
          style={{
            alignSelf: 'flex-start',
            marginTop: 8,
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: accentColor,
            backgroundColor: 'rgba(28, 187, 119, 0.2)'
          }}
        >
          <Text
            style={{
              fontFamily: fontFamily.font700,
              fontSize: 12,
              color: accentColor
            }}
          >
            {searchCountText(search)}
          </Text>
        </View>
      ) : null}
      {scopeText && search.totalMatches > 0 ? (
        <Text
          testID="settingsSearchScope"
          style={{
            marginTop: 6,
            fontFamily: fontFamily.font500,
            fontSize: 12,
            color: textMutedColor
          }}
        >
          {scopeText}
        </Text>
      ) : null}
    </View>
  );
};

export default SettingsSearchBar;
